import {
  BookingProductType,
  BookingState,
  BookingTariff,
  BookingTariffType,
  isBookingProductTypePackage,
  isBookingProductTypeTariff
} from '@products/models/booking.model';
import {
  PromoCode,
  RedeemedPromoCode,
  SetBookedProductsPromoCodeDiscount,
  SetUnredeemedPromoCode
} from '@products/models/smart-promotions';
import { AppConstants } from '@shared/app-constants';
import { cloneDeep, findIndex, forEach, isEmpty } from 'lodash';

const setBookedTariffsPromoCodeDiscount = (
  bookedTariffType: BookingTariffType,
  discountedPartialBookingTariffs: Partial<BookingTariff>[]
) => {
  forEach(bookedTariffType.tariffs, (bookedTariff) => {
    const bookedDiscountedTariff = discountedPartialBookingTariffs.find(
      ({ ticketPersonId, voucherCode, packageNumber, packageIndex }) =>
        ticketPersonId === bookedTariff.ticketPersonId &&
        voucherCode === bookedTariff.voucherCode &&
        packageNumber === bookedTariff.packageNumber &&
        packageIndex === bookedTariff.packageIndex
    );

    bookedTariff.discountedPrice = bookedDiscountedTariff.discountedPrice;
    bookedTariff.discountPercentage = bookedDiscountedTariff.discountPercentage;
    bookedTariff.promoCodeDiscounts = bookedDiscountedTariff.promoCodeDiscounts;
  });
};

/**
 * Set booking products promo code discount
 * @description Set booking products promo code discount
 * @param state
 * @param props
 * @returns State
 */
export const setBookedProductsPromoCodeDiscount = (state: BookingState, props: SetBookedProductsPromoCodeDiscount) => {
  const { discountedPartialBookingTariffs, appliedRedeemedPromoCodes } = props;

  if (isEmpty(discountedPartialBookingTariffs)) {
    return state;
  }

  const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);

  forEach(cloneStateProductsList, (bookingProductType) => {
    if (isBookingProductTypeTariff(bookingProductType)) {
      setBookedTariffsPromoCodeDiscount(bookingProductType, discountedPartialBookingTariffs);
    } else if (isBookingProductTypePackage(bookingProductType)) {
      bookingProductType.productTypes.forEach((bookingPackageProductType) => {
        if (isBookingProductTypeTariff(bookingPackageProductType)) {
          setBookedTariffsPromoCodeDiscount(bookingPackageProductType, discountedPartialBookingTariffs);
        }
      });
    }
  });

  return {
    ...state,
    list: cloneStateProductsList,
    promoCodes: appliedRedeemedPromoCodes
  };
};

/**
 * Set booking products unredeemed promo code
 * @description Set booking products unredeemed promo code
 * @param state
 * @param props
 * @returns State
 */
export const setUnredeemedPromoCode = (state: BookingState, props: SetUnredeemedPromoCode) => {
  const { promoCode, isRedeemedAndApplied } = props;

  const unredeemedPromoCode: RedeemedPromoCode = { promoCode, isRedeemedAndApplied, discountPercentage: null };

  return {
    ...state,
    promoCodes: [...state.promoCodes, unredeemedPromoCode]
  };
};

/**
 * Remove booking products promo code discount
 * @description Remove booking products promo code discount
 * @param state
 * @returns State
 */
export const removeBookedProductsPromoCodeDiscount = (state: BookingState) => {
  const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);

  forEach(cloneStateProductsList, (bookedProductType) => {
    if (isBookingProductTypeTariff(bookedProductType)) {
      forEach(bookedProductType.tariffs, (bookedTariff) => {
        bookedTariff.discountedPrice = 0;
        bookedTariff.discountPercentage = 0;
        bookedTariff.promoCodeDiscounts = null;
      });
    } else if (isBookingProductTypePackage(bookedProductType)) {
      forEach(bookedProductType.productTypes, (bookedPackageProductType) => {
        if (isBookingProductTypeTariff(bookedPackageProductType)) {
          forEach(bookedPackageProductType.tariffs, (bookedPackageTariff) => {
            bookedPackageTariff.discountedPrice = 0;
            bookedPackageTariff.discountPercentage = 0;
            bookedPackageTariff.promoCodeDiscounts = null;
          });
        }
      });
    }
  });

  return {
    ...state,
    list: cloneStateProductsList,
    promoCodes: []
  };
};

/**
 * Remove booking products promo code discount on changed product bookings
 * @description Remove booking products promo code discount on changed product bookings
 * @param state
 * @returns State
 */
export const removeBookedProductsPromoCodeDiscountOnChangedProductBookings = (state: BookingState) => {
  const { list, promoCodes } = state;

  const cloneStateProductsList = cloneDeep(list);
  const clonePromoCodes = cloneDeep(promoCodes);

  forEach(cloneStateProductsList, (bookedProductType) => {
    if (isBookingProductTypeTariff(bookedProductType)) {
      forEach(bookedProductType.tariffs, (bookedTariff) => {
        bookedTariff.discountedPrice = 0;
        bookedTariff.discountPercentage = 0;
        bookedTariff.promoCodeDiscounts = null;
      });
    } else if (isBookingProductTypePackage(bookedProductType)) {
      forEach(bookedProductType.productTypes, (bookedPackageProductType) => {
        if (isBookingProductTypeTariff(bookedPackageProductType)) {
          forEach(bookedPackageProductType.tariffs, (bookedPackageTariff) => {
            bookedPackageTariff.discountedPrice = 0;
            bookedPackageTariff.discountPercentage = 0;
            bookedPackageTariff.promoCodeDiscounts = null;
          });
        }
      });
    }
  });

  const updatedPromoCode = clonePromoCodes.map((promoCode) => ({ ...promoCode, isRedeemedAndApplied: false }));

  return {
    ...state,
    list: cloneStateProductsList,
    promoCodes: updatedPromoCode
  };
};

/**
 * Remove unredeemed promo code
 * @description Remove unredeemed promo code from promo code list
 * @param state
 * @returns State
 */
export const removeUnredeemedPromoCode = (state: BookingState, props: { removePromoCode: PromoCode }) => {
  const { removePromoCode } = props;

  if (isEmpty(removePromoCode)) {
    return state;
  }

  const updatedPromoCodes = cloneDeep(state.promoCodes);
  const removePromoCodeIndex = findIndex(
    updatedPromoCodes,
    (updatedPromoCode) => updatedPromoCode.promoCode === removePromoCode
  );

  if (removePromoCodeIndex === AppConstants.INDEX_NOT_FOUND) {
    return state;
  }

  updatedPromoCodes.splice(removePromoCodeIndex, 1);

  return {
    ...state,
    promoCodes: updatedPromoCodes
  };
};
